import React, { useState, useEffect, useRef } from 'react';
import { Trash2 } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import AlertNotification from '../../components/AlertNotification.js';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import TradeInStepper from '../../components/TradeInStepper.js';
import Pagination from '../../components/Pagination.js';
import noImage from '../../assets/img/brand/noImage.png';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import apiHelper from '../../apiHelper.js';
import TradeInImagePickerModal from './TradeInImagePickerModal.js';
import {
    getDeviceDefaultImage,
    isDeviceDefaultUrl,
    filenameFromUrl,
    withCurrentDeviceDefault,
    dedupePoolEntries,
    maxUploadIndex,
    tabForSource,
    isRemovedImage,
    SOURCE_ITEM,
} from './tradeInImages.js';

// The image picker, its helpers and the upload validation rules used to live here. Step 1's
// Listing Image now uses the identical picker over the same listing-wide upload pool
// (ERP-5022), so they moved to ./TradeInImagePickerModal.js and ./tradeInImages.js rather
// than being duplicated — a behaviour difference between the two levels is exactly what
// makes a pooled image resolve to the wrong file.

// ─── Custom Checkbox ──────────────────────────────────────────────────────────
function CustomCheckbox({ checked, onChange, label }) {
    return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14, fontFamily: 'Montserrat', fontWeight: 400, color: '#212529', userSelect: 'none' }}>
            <div
                onClick={onChange}
                style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    border: '1.5px solid #212529',
                    background: checked ? '#FFEB00' : 'white',
                    boxShadow: checked ? '0 1px 4px 0 rgba(0, 0, 0, 0.10)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                    transition: 'background 0.15s, box-shadow 0.15s',
                }}
            >
                {checked && (
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 5.5L4.5 8.5L9.5 2.5" stroke="#212529" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </div>
            {label}
        </label>
    );
}

// ─── Color Swatch Input ───────────────────────────────────────────────────────
function ColorCodeInput({ value, onChange, error }) {
    const isValidHex = /^[0-9A-Fa-f]{0,6}$/.test(value);
    const displayColor = value && value.length === 6 ? `#${value}` : '#E9ECEF';

    return (
        <div>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                border: `1px solid ${error ? '#DC3545' : '#6C757D'}`,
                borderRadius: 6,
                overflow: 'hidden',
                height: 40,
                background: 'white',
                padding: '10px'
            }}>
                {/* Color circle */}
                <div style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: displayColor,
                    border: '1px solid #CED4DA',
                    flexShrink: 0,
                    // marginLeft: 8,
                }} />
                {/* Hash symbol */}
                <span style={{
                    padding: '0 4px 0 6px',
                    color: '#212529',
                    fontSize: 14,
                    fontFamily: 'Montserrat',
                    userSelect: 'none',
                }}>
                    #
                </span>
                {/* Hex input */}
                <input
                    type="text"
                    value={value}
                    onChange={e => {
                        const v = e.target.value.replace(/[^0-9A-Fa-f]/g, '').slice(0, 6);
                        onChange(v);
                    }}
                    maxLength={6}
                    placeholder="FFFFFF"
                    style={{
                        border: 'none',
                        outline: 'none',
                        flex: 1,
                        fontSize: 14,
                        fontFamily: 'Montserrat',
                        background: 'transparent',
                        minWidth: 0,
                        color: '#212529'

                        // paddingRight: 8,
                    }}
                />
            </div>
            {error && (
                <div style={{ fontSize: 11, color: '#DC3545', fontFamily: 'Montserrat', marginTop: 2 }}>{error}</div>
            )}
        </div>
    );
}

// ─── Clearable Text Input ─────────────────────────────────────────────────────
function ClearableInput({ value, onChange, onBlur, placeholder, error, disabled }) {
    return (
        <div>
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    onBlur={e => onBlur && onBlur(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    style={{
                        width: '100%',
                        height: 40,
                        border: `1px solid ${error ? '#DC3545' : '#212529'}`,
                        borderRadius: 8,
                        fontSize: 14,
                        fontFamily: 'Montserrat',
                        padding: '10px',
                        paddingRight: 28,
                        background: disabled ? '#F8F9FA' : 'white',
                        outline: 'none',
                        color: '#212529',
                        boxSizing: 'border-box',
                    }}
                />
                {value && !disabled && (
                    <button
                        type="button"
                        onClick={() => onChange('')}
                        style={{
                            position: 'absolute',
                            right: 6,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 2,
                            display: 'flex',
                            alignItems: 'center',
                            color: '#212529',
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M9 3L3 9M3 3L9 9" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </button>
                )}
            </div>
            {error && (
                <div style={{ fontSize: 11, color: '#DC3545', fontFamily: 'Montserrat', marginTop: 2 }}>{error}</div>
            )}
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const PAGE_SIZE = 20;

export default function AddEditTradeInListingStep2() {
    const history = useHistory();
    const location = useLocation();

    const listing = location.state?.listing || {};
    const isEditMode = Boolean(location.state?.isEdit);
    const restoredItems = location.state?.listingItems;
    const restoredPricingMap = location.state?.pricingMap || null;
    const restoredOriginalConditions = location.state?.originalConditions || null;
    // The listing-wide upload pool and any removals Step 1 couldn't apply itself, both
    // forwarded through router state (ERP-5022). File objects survive history.pushState's
    // structured clone — already proven by listingItems, which has carried them since
    // before this ticket.
    const restoredPool = location.state?.imagePool || null;
    const carriedRemovals = location.state?.removedPoolImages || null;

    const [items, setItems] = useState([]);
    const [skusLoading, setSkusLoading] = useState(true);
    const [rowErrors, setRowErrors] = useState({});

    // Client-only rows (added via "Add Listing Item") get an id in this form so they
    // can never collide with a real backend sku_id, and so trash/save logic can tell them apart.
    const newRowCounterRef = useRef(0);

    // Listing-wide shared pool of uploaded images (ERP-5022) — a file uploaded anywhere on this
    // listing, by the listing itself in Step 1 or by any item here, shows up in every Upload tab.
    // Only the level that actually selects a file persists it on save (see confirmReselect +
    // Step 3 buildImageFields). Seeded from Step 1 when it forwarded one.
    const [sessionUploads, setSessionUploads] = useState(() => restoredPool || []);
    // Global counter for upload tile ids — keeps them unique across modal opens AND across the
    // two steps, which mint `upload-N` into the same pool. A counter that restarts at 0 hands
    // out an id the other step already used, and the two different files are then treated as
    // one image everywhere ids are compared.
    const uploadCounterRef = useRef(maxUploadIndex(restoredPool));
    // Deduped by filename as well as id, so the pool can't gain a second entry for a photo it
    // already holds (which would then render as two identical tiles in every Upload tab).
    const addSessionUpload = (entry) =>
        setSessionUploads(prev => (
            prev.some(e => e.id === entry.id || (entry.name && e.name === entry.name))
                ? prev
                : [...prev, entry]
        ));
    const removeSessionUpload = (id) =>
        setSessionUploads(prev => prev.filter(e => e.id !== id));

    // Removals of pool rows this step doesn't own — an item's image deleted from Step 1's
    // Upload tab arrives here, and Step 1's own rows deleted here ride back out on Back. Each
    // step applies the entries it owns and forwards the rest (see applyCarriedRemovals below).
    const [removedPoolImages, setRemovedPoolImages] = useState(() => carriedRemovals || []);

    // Re-point a row that's showing a device default at the CURRENT Listing Device's placeholder.
    const withRowDeviceDefault = (row) => {
        const image = withCurrentDeviceDefault(row.image, listing.listingDevice);
        return image === row.image ? row : { ...row, image };
    };

    const makeBlankRow = () => {
        newRowCounterRef.current += 1;
        // ERP-5022: a new listing item defaults to the brand placeholder for the listing's
        // device (not the SPU's first image). isPlaceholder marks "no real image chosen" so the
        // backend clears the item's image rather than snapshotting a source; isDeviceDefault also
        // opens the picker on the Upload tab with this non-deletable default tile selected.
        const defaultImage = { id: null, url: getDeviceDefaultImage(listing.listingDevice), isPlaceholder: true, isDeviceDefault: true };
        return {
            id: `new-${newRowCounterRef.current}`,
            newItemId: null,
            usedItemId: null,
            newPricing: null,
            usedPricing: null,
            storage: '',
            color: '',
            colorCode: 'ffffff',
            image: defaultImage,
            skuImages: [],
            uploadedImages: [],
            removedImageIds: [],
            conditionNew: true,
            conditionUsed: true,
            savedConditions: { new: false, used: false },
        };
    };

    // ── Load listing items: restore from Step 3, load saved items (edit mode), or start blank ──
    useEffect(() => {
        // Coming back from Step 3 — restore user-edited items directly (including an empty list)
        if (Array.isArray(restoredItems)) {
            // Re-resolve device defaults against the CURRENT Listing Device first. A row stores the
            // resolved asset URL, so a row created under Phone still carries the Phone placeholder
            // after the user goes back to Step 1 and switches to Watch — the row thumbnail (and the
            // Upload tab's default tile it feeds) would keep showing the old device's image.
            setItems(restoredItems.map(withRowDeviceDefault));
            // Reseed the counter past any restored "new-N" ids so freshly-added rows
            // never reuse an id already held by a restored row (they'd then share edits).
            let maxN = 0;
            restoredItems.forEach(r => {
                const match = typeof r.id === 'string' && r.id.match(/^new-(\d+)$/);
                if (match) maxN = Math.max(maxN, parseInt(match[1], 10));
            });
            newRowCounterRef.current = maxN;
            // Rebuild the shared upload pool (ERP-5022) from the forwarded pool plus every row's
            // uploads — already-saved ones included, so coming back from Step 3 shows the same
            // Upload tab the loader built rather than shrinking it to this session's fresh files.
            // The forwarded pool goes first: it's the only place Step 1's own uploads live, since
            // no item holds them. Deduped by filename (each row holds its own copy of a file it
            // uses, so one photo appears under several ids), falling back to id for the unnamed.
            const rowUploads = [];
            restoredItems.forEach(r => (r.uploadedImages || []).forEach(u => rowUploads.push(
                { ...u, source: u.source || SOURCE_ITEM }
            )));
            const pooled = dedupePoolEntries([...(restoredPool || []), ...rowUploads]);
            // Advance the upload id counter past every restored `upload-N`, from both sources,
            // so a fresh upload here can never reuse one Step 1 already handed out.
            uploadCounterRef.current = maxUploadIndex(pooled);
            setSessionUploads(pooled);
            if (restoredOriginalConditions) {
                originalConditionsRef.current = restoredOriginalConditions;
            }
            setSkusLoading(false);
            return;
        }

        const tradeinListingId = listing.tradein_listing_id;
        if (!isEditMode || !tradeinListingId) {
            // Create mode — start with a single blank, manually-editable row
            setItems([makeBlankRow()]);
            setSkusLoading(false);
            return;
        }

        async function fetchSavedItems() {
            try {
                // Every page, not just the first. This endpoint paginates at 20 by default, and
                // Step 2 sends the whole item list back on save — TradeInListingUpdateAPI
                // soft-deletes and delists from WooCommerce anything the payload omits, so a
                // truncated read here silently drops items 21+. It also caps the shared upload
                // pool below to whatever those first 20 items happened to hold.
                const PAGE_SIZE = 200;
                const fetchPage = async (page) => {
                    const res = await apiHelper(
                        `sales/trade-in-listings/${tradeinListingId}/items/?include_draft=true&page=${page}&page_size=${PAGE_SIZE}`,
                        'GET',
                    );
                    return res?.ok ? res.json() : null;
                };

                const savedData = await fetchPage(1);
                if (!savedData) { setSkusLoading(false); return; }

                const savedItems = Array.isArray(savedData) ? savedData : (savedData?.results || []);
                const totalPages = Array.isArray(savedData) ? 1 : (savedData?.total_pages || 1);
                for (let page = 2; page <= totalPages; page += 1) {
                    const nextData = await fetchPage(page);
                    savedItems.push(...(Array.isArray(nextData) ? nextData : (nextData?.results || [])));
                }
                // Backend still stores New/Used as two separate item rows for the same
                // Storage+Color — group them back into one UI row per Storage+Color combo
                // (not by sku_id, which is no longer a reliable/required per-item identity).
                const savedByVariant = {};
                savedItems.forEach(si => {
                    const key = `${(si.storage || '').trim().toLowerCase()}||${(si.color || '').trim().toLowerCase()}`;
                    if (!savedByVariant[key]) savedByVariant[key] = [];
                    savedByVariant[key].push(si);
                });

                // Listing-wide upload pool (ERP-5022): every Upload tab shows every image
                // uploaded anywhere on this listing, not only the ones that item saved itself —
                // which is what left a newly-added item's Upload tab empty on re-edit.
                // Keyed on the original filename, not the URL: each row stores its OWN
                // independent copy of a file it uses, so one photo arrives under several ids and
                // URLs. The filename is what identifies it across rows, and it's unique within a
                // listing because duplicate filenames are rejected at upload.
                //
                // Anything Step 1 already forwarded goes in first — that's where the LISTING's
                // own uploads live, and they carry source: 'listing' so a pick from one resolves
                // against tradein_listing_images rather than this item table.
                const poolByName = new Map();
                (restoredPool || []).forEach(entry => {
                    const name = entry.name || filenameFromUrl(entry.preview);
                    if (!name || poolByName.has(name)) return;
                    poolByName.set(name, { ...entry, name });
                });
                savedItems.forEach(si => (si.tradein_images || []).forEach(img => {
                    if (isDeviceDefaultUrl(img.url)) return;
                    // A row saved before filenames were stored falls back to the URL's last
                    // segment, so it still shows up here instead of being skipped entirely.
                    const name = img.filename || filenameFromUrl(img.url);
                    if (!name) return;
                    if (poolByName.has(name)) return;
                    poolByName.set(name, {
                        id: img.id, file: null, preview: img.url, name, isSaved: true,
                        source: SOURCE_ITEM,
                    });
                }));

                const merged = Object.keys(savedByVariant).map(key => {
                    const saved = savedByVariant[key];
                    // Only items with a non-null tradein_condition are "checked" (active).
                    // Items saved with tradein_condition: null are drafts (previously unchecked).
                    const activeItems = saved.filter(si => si.tradein_condition != null);
                    const first = saved[0];
                    const conditions = activeItems.map(s => (s.tradein_condition || '').toLowerCase());
                    // New and Used are two separate backend rows for this Storage+Color combo —
                    // keep both of their real ids (not just `first`'s) so Step 3 can send each
                    // condition's own id back to the backend. Losing either one here means a
                    // Storage/Color edit can only preserve one condition's Open ID and silently
                    // delists+recreates the other (ERP-4999).
                    const newActive  = activeItems.find(si => (si.tradein_condition || '').toLowerCase() === 'new');
                    const usedActive = activeItems.find(si => (si.tradein_condition || '').toLowerCase() === 'used');
                    // A device-default snapshot (e.g. taken while an earlier Listing Device was
                    // selected) isn't a real chosen image — treat it exactly like "no image saved"
                    // so it falls through to the CURRENT device's default instead of sticking around
                    // as a stale, mismatched "real" image.
                    const rawImageUrl = first.tradein_image_url || first.user_image_name || null;
                    const imageUrl = isDeviceDefaultUrl(rawImageUrl) ? null : rawImageUrl;
                    // If a proper snapshot exists, send id=null so backend uses the
                    // snapshot fallback and never re-resolves the live source.
                    // For legacy items (user_image_name only), keep the live id so
                    // the backend can create a first-time snapshot.
                    const hasSnapshot = Boolean(imageUrl && first.tradein_image_url);
                    // ERP-5022: saved items with no stored image fall back to the device default
                    // (not the SPU's first image), matching a newly-created row's default.
                    const defaultImage = { id: null, url: getDeviceDefaultImage(listing.listingDevice), isPlaceholder: true, isDeviceDefault: true };
                    const uploadHistory = (first.tradein_images || []).filter(img => !isDeviceDefaultUrl(img.url));
                    // Confirmed via the actual API response: `tradein_image_url` (the active image)
                    // is a freshly re-encoded snapshot with its OWN filename — it never matches any
                    // `tradein_images[].url`, even for this same item, so URL matching can't link
                    // them. But the backend does echo the ORIGINAL filename back as
                    // `user_image_name`, which does match one of `tradein_images[].filename` — use
                    // that to resolve which history row is actually active, so it opens on that
                    // real (deletable) tile instead of a separate "current image" pin.
                    // Prefer a row this item owns (so a later removal is scoped to it), then fall
                    // back to the listing-wide pool — with the shared popup an item can be using
                    // a file that another item uploaded, in which case no row of its own matches.
                    const ownUpload = first.user_image_name
                        ? uploadHistory.find(img => img.filename === first.user_image_name)
                        : null;
                    const pooledUpload = (!ownUpload && first.user_image_name)
                        ? poolByName.get(first.user_image_name)
                        : null;
                    // Normalised to {id, url, source} — pool entries are already in tile shape
                    // (`preview`), and carry the source that says which table their id is in.
                    // A file the LISTING uploaded is a legitimate match here (ERP-5022), and
                    // resolving it as an item id would save a different picture entirely.
                    const matchedUpload = ownUpload
                        ? { id: ownUpload.id, url: ownUpload.url, source: SOURCE_ITEM }
                        : (pooledUpload
                            ? { id: pooledUpload.id, url: pooledUpload.preview, source: pooledUpload.source || SOURCE_ITEM }
                            : null);
                    return {
                        // Used only as a stable client-side key for this editing session —
                        // no longer sent to the backend as sku_id.
                        id: first.tradein_listing_item_id,
                        // Real backend ids for each condition's own row — see comment above.
                        newItemId:  newActive  ? newActive.tradein_listing_item_id  : null,
                        usedItemId: usedActive ? usedActive.tradein_listing_item_id : null,
                        // Previously-saved Base Price + Deductions per condition (ERP-5008) — carried
                        // through so Step 3 can prefill them for any row whose Open ID is being
                        // preserved, instead of resetting pricing to blank on every re-edit.
                        newPricing:  newActive  ? { mode: newActive.base_price_mode,  value: newActive.base_price,  deductions: newActive.deductions  || [] } : null,
                        usedPricing: usedActive ? { mode: usedActive.base_price_mode, value: usedActive.base_price, deductions: usedActive.deductions || [] } : null,
                        storage: first.storage || '',
                        color: first.color || '',
                        colorCode: first.color_code || 'ffffff',
                        conditionNew: conditions.includes('new'),
                        conditionUsed: conditions.includes('used'),
                        image: matchedUpload
                            ? { id: matchedUpload.id, url: matchedUpload.url, source: matchedUpload.source, tab: tabForSource(matchedUpload.source) }
                            // No upload row resolved. `user_image_name` is still the reliable
                            // "this came from an upload" signal — the backend keeps it set to the
                            // active image's original filename and clears it whenever the image
                            // came from the SPU tab instead — so use it to open the right tab
                            // rather than defaulting everything to SPU.
                            : (imageUrl
                                ? { id: hasSnapshot ? null : null, url: imageUrl, tab: first.tradein_image_tab || (first.user_image_name ? 'tradein' : 'spu') }
                                : defaultImage),
                        skuImages: [],
                        // Previously-uploaded images for this item (from tradein_listing_item_images) —
                        // shown as history in the Reselect modal's Upload tab. isSaved: true means no
                        // re-encoding/re-persisting is needed on the next save (only new uploads are).
                        uploadedImages: uploadHistory.map(img => ({
                            id: img.id,
                            file: null,
                            preview: img.url,
                            // Same fallback as the pool above, so a row with no stored filename
                            // still gets a name to show on hover instead of a blank tooltip.
                            name: img.filename || filenameFromUrl(img.url),
                            isSaved: true,
                            source: SOURCE_ITEM,
                        })),
                        // Ids from the list above that the user removes while editing — sent to the
                        // backend on save so the rows are actually deleted, not just hidden.
                        removedImageIds: [],
                        // Track which conditions were saved — determines new vs old in Step 3
                        savedConditions: {
                            new: conditions.includes('new'),
                            used: conditions.includes('used'),
                        },
                    };
                });
                // A listing must always have at least one row — seed a blank one if nothing was saved yet
                if (merged.length === 0) merged.push(makeBlankRow());
                setItems(merged);
                // Seed the shared pool with everything already saved on this listing — the
                // listing's own uploads included, via restoredPool — so an item added on re-edit
                // opens with the same Upload tab as every other one instead of just the device
                // default. Deduped by filename on the way in (see poolByName), so the independent
                // per-row copies of one photo collapse to a single tile.
                const seededPool = Array.from(poolByName.values());
                setSessionUploads(seededPool);
                uploadCounterRef.current = maxUploadIndex(seededPool);
                // Snapshot original conditions for uncheck detection
                const snap = {};
                merged.forEach(r => { snap[r.id] = { conditionNew: r.conditionNew, conditionUsed: r.conditionUsed }; });
                originalConditionsRef.current = snap;
            } catch (err) {
                console.error('Failed to fetch saved listing items:', err);
            } finally {
                setSkusLoading(false);
            }
        }
        fetchSavedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Removals that arrived from Step 1 aimed at ITEM rows — the user deleted an item's upload
    // from Step 1's shared Upload tab, and only an item payload can actually delete it
    // (ERP-5022). Fold them into the owning items once those have loaded. Listing-owned entries
    // stay in removedPoolImages and ride back to Step 1 on Back, which is where they belong.
    useEffect(() => {
        if (skusLoading) return;
        const carried = removedPoolImages.filter(r => r.source === SOURCE_ITEM);
        if (!carried.length) return;
        const mine = carried.map(r => r.id);
        setItems(prev => prev.map(it => {
            const holdsRemoved = (it.uploadedImages || []).some(u => isRemovedImage(
                { id: u.id, url: u.preview, name: u.name }, carried,
            ));
            const imageIsRemoved = isRemovedImage(it.image, carried);
            if (!holdsRemoved && !imageIsRemoved) return it;
            const existing = it.removedImageIds || [];
            return {
                ...it,
                uploadedImages: (it.uploadedImages || []).filter(u => !isRemovedImage(
                    { id: u.id, url: u.preview, name: u.name }, carried,
                )),
                image: imageIsRemoved
                    ? { id: null, url: getDeviceDefaultImage(listing.listingDevice), isPlaceholder: true, isDeviceDefault: true }
                    : it.image,
                removedImageIds: [...existing, ...mine.filter(id => !existing.includes(id))],
            };
        }));
        setSessionUploads(prev => prev.filter(e => !isRemovedImage(
            { id: e.id, url: e.preview, name: e.name }, carried,
        )));
        setRemovedPoolImages(prev => prev.filter(r => r.source !== SOURCE_ITEM));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skusLoading]);

    const [page, setPage] = useState(1);
    const [notification, setNotification] = useState({ show: false, type: '', message: '' });
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(location.state?.isDirty || false);
    const [nextLocation, setNextLocation] = useState(null);
    const [isDiscarding, setIsDiscarding] = useState(false);

    // Uncheck-condition confirmation modal (edit mode only)
    const [uncheckConfirmOpen, setUncheckConfirmOpen] = useState(false);
    const [pendingUncheck, setPendingUncheck] = useState(null); // { rowId, field }

    // Store original conditions snapshot (set once when items load in edit mode)
    const originalConditionsRef = useRef(null); // { [skuId]: { conditionNew, conditionUsed } }

    // Image reselect modal state
    const [reselectModal, setReselectModal] = useState(null); // { rowId, skuId }

    const isDirtyRef = useRef(isDirty);
    const unblockRef = useRef(null);
    // rowKeys (matching Step 3's `${itemId}-new` / `${itemId}-used`) dropped from the pricing map
    // forwarded to Step 3.
    //
    // Nothing the user does on THIS step invalidates pricing any more: Step 3's Base Price and
    // Deductions only reset when the Listing Device changes in Step 1 (which is what the deduction
    // template is keyed on — see Step 1's `deviceChanged`). Editing Storage/Color, swapping an
    // image, or unchecking and re-checking New/Used all leave the row's already-entered price and
    // deductions exactly as they were.
    //
    // The one remaining entry is a DELETED row, and that isn't a pricing rule — it's an id-reuse
    // guard. A client-only row's id (`new-N`) is reseeded from the surviving rows on every return
    // to this step, so deleting the highest-numbered one frees its id for the next "Add Listing
    // Item"; without dropping its keys here, that fresh row would inherit the deleted row's price.
    const invalidatedRowKeysRef = useRef(new Set());
    useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

    // Forwards the in-progress Step 3 pricing map minus only the rowKeys actually invalidated
    // this session (see above — deleted rows only).
    const getForwardedPricingMap = () => {
        if (!restoredPricingMap || invalidatedRowKeysRef.current.size === 0) return restoredPricingMap;
        const next = { ...restoredPricingMap };
        invalidatedRowKeysRef.current.forEach(key => { delete next[key]; });
        return next;
    };

    // Navigation guard
    useEffect(() => {
        const unblock = history.block((loc) => {
            if (isDirtyRef.current && !isDiscarding) {
                setNextLocation(loc);
                setDiscardModalOpen(true);
                return false;
            }
        });
        unblockRef.current = unblock;
        return () => {
            unblock();
            unblockRef.current = null;
        };
    }, [history, isDiscarding]);

    // ── Before-unload (browser tab/window close) ──
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty && !isDiscarding) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty, isDiscarding]);

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
    };

    // ── Helpers ────────────────────────────────────────────────────────────────
    // No field edited here invalidates Step 3's pricing — not Storage/Color, not the image, and
    // not the New/Used checkboxes. Unchecking a condition hides its row in Step 3, but its Base
    // Price and Deductions stay in the forwarded map, so re-checking brings them back untouched
    // rather than blank. Only a Listing Device change in Step 1 rebuilds pricing.
    const updateItem = (id, field, value) => {
        setIsDirty(true);

        setItems(prev =>
            prev.map(r =>
                r.id === id ? { ...r, [field]: value } : r
            )
        );

        setRowErrors(prev => {
            const clone = { ...prev };

            if (clone[id]) {
                clone[id] = {
                    ...clone[id],
                    [field]: ''
                };
            }

            return clone;
        });
    };


    // Intercepts condition checkbox toggle — shows confirmation before unchecking a saved condition
    const handleConditionToggle = (rowId, field) => {
        const currentValue = items.find(r => r.id === rowId)?.[field];
        const isSavedCondition = isEditMode &&
            originalConditionsRef.current?.[rowId]?.[field] === true;

        if (currentValue && isSavedCondition) {
            setPendingUncheck({ rowId, field });
            setUncheckConfirmOpen(true);
        } else {
            updateItem(rowId, field, !currentValue);
        }
    };

    // ── Add / remove rows ─────────────────────────────────────────────────────
    const addBlankRow = () => {
        setItems(prev => [...prev, makeBlankRow()]);
        setIsDirty(true);
        // A brand-new row's rowKey can't already exist in the restored pricing map, so there's
        // nothing to invalidate — every other row's pricing is unaffected.
    };

    // Trash icon always removes the whole row. If it's currently checked (New/Used),
    // show the delist confirmation first — the row is only removed once confirmed.
    const removeRow = (rowId) => {
        setItems(prev => prev.filter(r => r.id !== rowId));
        setIsDirty(true);
        invalidatedRowKeysRef.current.add(`${rowId}-new`);
        invalidatedRowKeysRef.current.add(`${rowId}-used`);
    };

    const handleDeleteRow = (rowId) => {
        const row = items.find(r => r.id === rowId);
        const isActive = Boolean(row?.conditionNew || row?.conditionUsed);

        if (isActive) {
            setPendingUncheck({ rowId, field: 'delete' });
            setUncheckConfirmOpen(true);
        } else {
            removeRow(rowId);
        }
    };

    // ── Pagination ─────────────────────────────────────────────────────────────
    const totalPages = Math.ceil(items.length / PAGE_SIZE);
    const pagedItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // ── Validate ───────────────────────────────────────────────────────────────
    const validate = () => {
        // All rows can now be deleted (including the last one) — block moving on until
        // there's at least one to carry into Pricing.
        if (items.length === 0) {
            showNotification('error', 'Please add at least one Item');
            return false;
        }

        let hasError = false;
        const newRowErrors = {};

        // Validate all items: storage + color required; the Storage+Color combination must be unique
        // (a single row already covers both New and Used via its own checkboxes)
        const combos = {};
        items.forEach(item => {
            if (!item.storage.trim() || !item.color.trim()) return;
            const key = `${item.storage.trim().toLowerCase()}||${item.color.trim().toLowerCase()}`;
            if (!combos[key]) combos[key] = [];
            combos[key].push(item.id);
        });

        items.forEach(item => {
            const errs = {};
            if (!item.storage.trim()) errs.storage = 'Storage is required';
            if (!item.color.trim()) errs.color = 'Color is required';
            if (!errs.storage && !errs.color) {
                const key = `${item.storage.trim().toLowerCase()}||${item.color.trim().toLowerCase()}`;
                if (combos[key]?.length > 1) errs.duplicate = 'Duplicated';
            }
            if (Object.keys(errs).length > 0) {
                newRowErrors[item.id] = errs;
                hasError = true;
            }
        });

        setRowErrors(newRowErrors);

        if (hasError) {
            showNotification('error', 'Please fix the errors below');
            return false;
        }

        const anyConditionChecked = items.some(item => item.conditionNew || item.conditionUsed);
        if (!anyConditionChecked) {
            showNotification('error', 'Please check at least one Item');
            return false;
        }

        return true;
    };

    // ── Actions ────────────────────────────────────────────────────────────────
    const goToStep3 = () => {
        if (unblockRef.current) {
            unblockRef.current();
            unblockRef.current = null;
        }
        history.push(isEditMode ? '/trade-in-listing/edit-listing/pricing' : '/trade-in-listing/add-listing/pricing', {
            listing,
            listingItems: items,
            isEdit: isEditMode,
            isDirty,
            pricingMap: getForwardedPricingMap(),
            originalConditions: originalConditionsRef.current,
            // The shared pool and any not-yet-applied removals travel with the wizard so
            // Step 3 can hand them back on Back without them being rebuilt from scratch.
            imagePool: sessionUploads,
            removedPoolImages,
        });
    };

    const handleNext = () => {
        if (!validate()) return;
        goToStep3();
    };

    const handleBack = () => {
        if (unblockRef.current) {
            unblockRef.current();
            unblockRef.current = null;
        }
        const path = isEditMode ? '/trade-in-listing/edit-listing' : '/trade-in-listing/add-listing';
        // imagePool + removedPoolImages go back to Step 1 too: files uploaded here belong to the
        // whole listing, and a listing-owned row deleted here can only be removed by Step 1.
        history.push(path, {
            listing, listingItems: items, isEdit: isEditMode, isDirty,
            pricingMap: getForwardedPricingMap(),
            imagePool: sessionUploads,
            removedPoolImages,
        });
    };

    const handleDiscard = () => {
        if (isDirty) setDiscardModalOpen(true);
        else history.push('/trade-in-listing');
    };

    const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            setIsDiscarding(true);
            setIsDirty(false);
            setDiscardModalOpen(false);
            setTimeout(() => {
                if (nextLocation) {
                    const { pathname, state } = nextLocation.location || nextLocation;
                    const unblock = history.block(() => null);
                    history.push(pathname, state);
                    setTimeout(() => unblock(), 0);
                    setNextLocation(null);
                } else {
                    history.push('/trade-in-listing', { successMessage: 'Listing Discarded Successfully' });
                }
            }, 100);
        } else {
            setDiscardModalOpen(false);
            setNextLocation(null);
        }
    };

    // ── Blur validation (all rows) ────────────────────────────────────────────
    const handleBlur = (rowId, field, currentValue) => {
        const messages = { storage: 'Storage is required', color: 'Color is required' };
        const isEmpty = !currentValue?.trim();
        setRowErrors(prev => {
            const rowErrs = { ...(prev[rowId] || {}) };
            if (isEmpty) rowErrs[field] = messages[field];
            else delete rowErrs[field];
            return { ...prev, [rowId]: rowErrs };
        });
    };

    // ── Image reselect ────────────────────────────────────────────────────────
    const openReselect = (rowId) => setReselectModal({ rowId });

    const confirmReselect = (img, uploads, removed) => {
        if (img) updateItem(reselectModal.rowId, 'image', img);
        // `uploads` is already scoped (see the modal's Confirm handler) to this item's own saved
        // history + its own fresh uploads + whichever pool entry got actively selected — so every
        // file this item uploaded persists, not just the one currently picked as the active image,
        // without also attaching every other item's pooled uploads here (ERP-5022).
        const savedUploads = (uploads || []).filter(u => u.isSaved);
        const newUploads = (uploads || []).filter(u => !u.isSaved && u.file);
        updateItem(reselectModal.rowId, 'uploadedImages', [...savedUploads, ...newUploads]);
        // Accumulate across reopens of the modal — each session only reports its own removals,
        // but every one of them still has to reach the backend on the eventual save.
        if (removed?.length) {
            // Only the level that owns a row can delete it through its own payload (ERP-5022).
            // Anything the LISTING owns is stashed and forwarded to Step 1 on Back, which does.
            const removedSavedIds = removed.filter(r => r.source === SOURCE_ITEM).map(r => r.id);
            const theirs = removed.filter(r => r.source !== SOURCE_ITEM);
            if (theirs.length) {
                setRemovedPoolImages(prev => [
                    ...prev,
                    ...theirs.filter(t => !prev.some(p => p.id === t.id && p.source === t.source)),
                ]);
            }
            // The pool tile goes regardless of who owns the row — it's gone from every Upload
            // tab, and so is any other entry holding the same picture under a different id.
            setSessionUploads(prev => prev.filter(e => !isRemovedImage(
                { id: e.id, url: e.preview, name: e.name }, removed,
            )));
            if (!removedSavedIds.length) { setReselectModal(null); return; }
            const existing = items.find(r => r.id === reselectModal.rowId)?.removedImageIds || [];
            updateItem(reselectModal.rowId, 'removedImageIds', [
                ...existing,
                ...removedSavedIds.filter(id => !existing.includes(id)),
            ]);
            // Verified against the actual API response: the backend does NOT dedupe uploads
            // across items — two items that each save "2.jpg" end up with two independent
            // tradein_listing_item_images rows (different ids/files), so removing an id here can
            // only affect another item if that item still shares the same NOT-YET-SAVED pool
            // entry (same client-side id) — once both have independently saved, their copies are
            // unrelated and this is a no-op for them. Still worth clearing pre-save so an
            // in-session removal doesn't leave a stale pool tile pointing at nothing.
            setItems(prev => prev.map(it => {
                if (it.id === reselectModal.rowId) return it;
                const stillHasRemoved = (it.uploadedImages || []).some(u => isRemovedImage(
                    { id: u.id, url: u.preview, name: u.name }, removed,
                ));
                const imageIsRemoved = isRemovedImage(it.image, removed);
                if (!stillHasRemoved && !imageIsRemoved) return it;
                const itExisting = it.removedImageIds || [];
                return {
                    ...it,
                    uploadedImages: (it.uploadedImages || []).filter(u => !isRemovedImage(
                        { id: u.id, url: u.preview, name: u.name }, removed,
                    )),
                    image: imageIsRemoved
                        ? { id: null, url: getDeviceDefaultImage(listing.listingDevice), isPlaceholder: true, isDeviceDefault: true }
                        : it.image,
                    removedImageIds: [...itExisting, ...removedSavedIds.filter(id => !itExisting.includes(id))],
                };
            }));
        }
        setReselectModal(null);
    };

    // ── Render ────────────────────────────────────────────────────────────────
    const pageContainerStyle = {
        // padding: '20px 24px',
        fontFamily: 'Montserrat',
        minHeight: '100%',
        // background: '#F8F9FA',
    };

    const labelStyle = {
        fontSize: 13,
        fontWeight: 600,
        color: '#212529',
        fontFamily: 'Montserrat',
        marginBottom: 4,
        display: 'block',
    };

    // Column widths
    const COL = {
        delete: 40,
        skuName: '20%',
        conditionL1: 150,
        image: 120,
        storage: '14%',
        color: '14%',
        colorCode: 130,
    };

    const thStyle = {
        fontSize: 12,
        fontWeight: 700,
        fontFamily: 'Montserrat',
        color: '#212529',
        padding: '10px',
        background: '#E9ECEF',
        borderBottom: '1px solid #DEE2E6',
        whiteSpace: 'nowrap',
    };

    const tdStyle = (hasErr, rowBg = 'white') => ({
        padding: '10px',
        borderBottom: '1px solid #E9ECEF',
        verticalAlign: 'center',
        background: rowBg,
    });

    return (
        <div style={pageContainerStyle}>
            {/* Notification */}
            {notification.show && (
                <AlertNotification
                    type={notification.type}
                    action="create"
                    recordName="Listing"
                    message={notification.message}
                    onClose={() => setNotification(prev => ({ ...prev, show: false }))}
                />
            )}

            {/* Discard Confirmation */}
            {discardModalOpen && (
                <DiscardConfirmationModal
                    isOpen={discardModalOpen}
                    onConfirm={() => handleDiscardConfirm(true)}
                    onCancel={() => handleDiscardConfirm(false)}
                    entityName="Trade-In Listing"
                />
            )}

            {/* Uncheck Condition Confirmation Modal */}
            {uncheckConfirmOpen && (
                <>
                    <div className="drawer-overlay" />
                    <div
                        className="entity-drawer modal-popup-cls"
                        style={{
                            maxWidth: 650,
                            margin: '0 auto',
                            background: 'white',
                            borderRadius: 12,
                            zIndex: 1050,
                            padding: '20px 25px',
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
                            height: 'auto',
                            right: 'auto',
                            overflow: 'inherit',
                        }}
                    >
                        {/* Close button */}
                        <div className="d-flex justify-content-end align-items-center pt-0 pe-0">
                            <button
                                type="button"
                                onClick={() => { setUncheckConfirmOpen(false); setPendingUncheck(null); }}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', outline: 'none' }}
                                onFocus={e => e.target.blur()}
                            >
                                <svg width="18" height="18" viewBox="0 0 18 18">
                                    <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="black" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                        {/* Title */}
                        <h3 style={{ textAlign: 'left', color: '#212529', fontSize: '20px', fontWeight: '600', lineHeight: 'normal', fontFamily: 'Montserrat', marginBottom: '10px', paddingTop: '20px' }}>
                            You are going to delist these Listing Items
                        </h3>
                        {/* Body */}
                        <p style={{ paddingBottom: '20px', marginBottom: 0, textAlign: 'left', color: '#6C757D', fontSize: '14px', fontWeight: '400', lineHeight: '20px', fontFamily: 'Montserrat' }}>
                            You have unchecked these Listing Items. Once saved, these Listing Items will be treated as delisted, and announce the downstream platforms.
                        </p>
                        {/* Footer */}
                        <div className="drawer-footer" style={{ paddingTop: '10px', paddingBottom: '10px' }}>
                            <button
                                type="button"
                                onClick={() => { setUncheckConfirmOpen(false); setPendingUncheck(null); }}
                                style={{ display: 'flex', height: '34px', minWidth: '100px', padding: '10px 0px', justifyContent: 'center', alignItems: 'center', gap: '10px', borderRadius: '8px', border: '1px solid #212529', background: '#FFF', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)', color: '#212529', fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (pendingUncheck) {
                                        if (pendingUncheck.field === 'delete') {
                                            removeRow(pendingUncheck.rowId);
                                        } else {
                                            updateItem(pendingUncheck.rowId, pendingUncheck.field, false);
                                        }
                                        setPendingUncheck(null);
                                    }
                                    setUncheckConfirmOpen(false);
                                }}
                                style={{ display: 'flex', height: '34px', minWidth: '100px', padding: '10px 0px', justifyContent: 'center', alignItems: 'center', gap: '10px', borderRadius: '8px', background: '#212529', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)', color: '#FFF', fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Image picker — shared with Step 1's Listing Image (ERP-5022) */}
            {reselectModal && (
                <TradeInImagePickerModal
                    imageLabel="Listing Item Image"
                    ownSource={SOURCE_ITEM}
                    images={listing.spu?.images || []}
                    initialSelected={items.find(r => r.id === reselectModal.rowId)?.image}
                    initialUploadedImages={items.find(r => r.id === reselectModal.rowId)?.uploadedImages || []}
                    sessionUploads={sessionUploads}
                    onAddSessionUpload={addSessionUpload}
                    onRemoveSessionUpload={removeSessionUpload}
                    uploadCounterRef={uploadCounterRef}
                    deviceDefaultImage={getDeviceDefaultImage(listing.listingDevice)}
                    // Editing a listing must not flag the files it already owns.
                    excludeListingId={listing.tradein_listing_id}
                    onCancel={() => setReselectModal(null)}
                    onConfirm={confirmReselect}
                />
            )}

            {/* Stepper */}
            <TradeInStepper currentStep={2} />

            {/* Page card */}
            <div style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

                {/* Page header row */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px',
                    borderBottom: '1px solid #E9ECEF',
                    background: 'white',
                    borderRadius: '8px'
                }}>
                    <h5 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'Montserrat', color: '#212529' }}>
                        Listing Items
                    </h5>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button type="button" className="btn dark-border" onClick={handleDiscard}
                            style={{
                                borderRadius: '8px',
                                background: 'none',

                                /* Shadow/Small */
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                height: '34px',
                                minWidth: '100px',
                                padding: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 600,
                                fontSize: '12px',
                                display: 'flex',
                                color: '#212529'
                            }}>
                            Discard
                        </button>
                        <button type="button" className="btn dark-border" onClick={handleBack}
                            style={{
                                borderRadius: '8px',
                                background: 'none',
                                color: '#212529',
                                /* Shadow/Small */
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                height: '34px',
                                minWidth: '100px',
                                padding: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 600,
                                fontSize: '12px',
                                display: 'flex'
                            }}>
                            Back
                        </button>
                        <button type="button" className="btn dark-button" onClick={handleNext}
                            style={{
                                borderRadius: '8px',
                                background: '#212529',

                                /* Shadow/Small */
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                height: '34px',
                                minWidth: '100px',
                                padding: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 600,
                                fontSize: '12px',
                                display: 'flex',
                                color: "white"
                            }}>
                            Next
                        </button>
                    </div>
                </div>

                {/* Subtitle */}

                {/* Table */}
                <div style={{ overflowX: 'auto', background: 'white', marginTop: '13px', borderRadius: '12px' }}>
                    <p style={{ padding: '15px 20px', borderRadius: '12px 12px 0 0', background: '#F8F9FA', fontSize: 14, color: '#212529', fontFamily: 'Montserrat', fontWeight: 600 }}>
                        Listing Item Info
                    </p>
                    <div style={{ padding: '0px 20px 20px 20px' }}>

                        {items.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-content">
                                <div className="icon">
                                    <img src={noBrandImage} alt="empty" style={{ width: 120, height: 120 }} />
                                </div>
                                <h1 className="no-item-title mt-2">No Listing Item Exists</h1>
                                <p className="no-item-text">Nothing here yet.</p>
                            </div>
                        </div>
                        ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800, marginTop: '20px' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle, width: COL.delete, textAlign: 'center' }} />
                                    <th style={{ ...thStyle, width: COL.skuName }}>Listing Model</th>
                                    <th style={{ ...thStyle, width: COL.image }}>Image</th>
                                    <th style={{ ...thStyle, width: COL.storage }}>Storage</th>
                                    <th style={{ ...thStyle, width: COL.color }}>Color</th>
                                    <th style={{ ...thStyle, width: COL.colorCode }}>Color Code</th>
                                    <th style={{ ...thStyle, width: COL.conditionL1 }}>Condition</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedItems.map((row, rowIdx) => {
                                    const errs = rowErrors[row.id] || {};
                                    const hasDuplicate = Boolean(errs.duplicate);
                                    const hasRowErr = Object.keys(errs).length > 0;
                                    const rowBg = rowIdx % 2 === 0 ? 'white' : '#F8F9FA';

                                    return (
                                        <tr key={row.id}>
                                            {/* Delete row — every row can be removed, including the last one left */}
                                            <td style={{ ...tdStyle(false, rowBg), width: COL.delete, textAlign: 'center' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteRow(row.id)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex', alignItems: 'center' }}
                                                >
                                                    <Trash2 size={16} color="#212529" />
                                                </button>
                                            </td>

                                            {/* Listing Model */}
                                            <td style={{ ...tdStyle(hasRowErr, rowBg), verticalAlign: 'center' }}>
                                                <div style={{ fontSize: 12, fontFamily: 'Montserrat', color: '#212529', fontWeight: 400 }}>
                                                    {listing.listingModel}
                                                </div>
                                            </td>

                                            {/* Image */}
                                            <td style={{ ...tdStyle(false, rowBg), width: COL.image }}>
                                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                                    <div style={{
                                                        width: 32,
                                                        height: 32,
                                                        // border: '1px solid #E9ECEF',
                                                        // borderRadius: 6,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: '#FAFAFA',
                                                        overflow: 'hidden',
                                                    }}>
                                                        <img
                                                            src={row.image?.url || noImage}
                                                            alt={listing.listingModel}
                                                            onError={e => { e.target.onerror = null; e.target.src = noImage; }}
                                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => openReselect(row.id)}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            fontSize: 12,
                                                            fontFamily: 'Montserrat',
                                                            fontWeight: 600,
                                                            color: '#212529',
                                                            background: 'none',
                                                            border: '1px solid #212529',
                                                            borderRadius: 8,
                                                            padding: '10px',
                                                            cursor: 'pointer',
                                                            minWidth: '70px',
                                                            height: '34px',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        Reselect
                                                    </button>
                                                </div>
                                            </td>

                                            {/* Storage */}
                                            <td style={tdStyle(Boolean(errs.storage || hasDuplicate), rowBg)}>
                                                <ClearableInput
                                                    value={row.storage}
                                                    onChange={val => updateItem(row.id, 'storage', val)}
                                                    onBlur={(val) => handleBlur(row.id, 'storage', val)}
                                                    // placeholder="e.g. 128GB"
                                                    error={errs.duplicate ? errs.duplicate : errs.storage}
                                                />
                                            </td>

                                            {/* Color */}
                                            <td style={tdStyle(Boolean(errs.color || hasDuplicate), rowBg)}>
                                                <ClearableInput
                                                    value={row.color}
                                                    onChange={val => updateItem(row.id, 'color', val)}
                                                    onBlur={(val) => handleBlur(row.id, 'color', val)}
                                                    // placeholder="e.g. Black"
                                                    error={errs.duplicate ? errs.duplicate : errs.color}
                                                />
                                            </td>

                                            {/* Color Code */}
                                            <td style={{ ...tdStyle(false, rowBg), width: COL.colorCode, padding: '10px' }}>
                                                <ColorCodeInput
                                                    value={row.colorCode}
                                                    onChange={val => updateItem(row.id, 'colorCode', val)}
                                                    error={errs.colorCode}
                                                />
                                            </td>

                                            {/* Condition — New / Used */}
                                            <td style={{ ...tdStyle(false, rowBg), width: COL.conditionL1 }}>
                                                <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
                                                    <CustomCheckbox
                                                        checked={row.conditionNew}
                                                        onChange={() => handleConditionToggle(row.id, 'conditionNew')}
                                                        label="New"
                                                    />
                                                    <CustomCheckbox
                                                        checked={row.conditionUsed}
                                                        onChange={() => handleConditionToggle(row.id, 'conditionUsed')}
                                                        label="Used"
                                                    />
                                                </div>
                                            </td>

                                        </tr>
                                    );
                                })}

                                {pagedItems.length === 0 && (
                                    <tr>
                                        <td colSpan={7} style={{ textAlign: 'center', padding: '40px 0', color: '#ADB5BD', fontSize: 14, fontFamily: 'Montserrat' }}>
                                            No Listing Items added yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        )}

                        {/* Add Listing Item */}
                        <button
                            type="button"
                            onClick={addBlankRow}
                            style={{
                                marginTop: 16,
                                borderRadius: '8px',
                                background: 'none',
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                height: '34px',
                                minWidth: '140px',
                                padding: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 600,
                                fontSize: '12px',
                                fontFamily: 'Montserrat',
                                display: 'flex',
                                color: '#212529',
                                border: '1px solid #212529',
                                cursor: 'pointer',
                            }}
                        >
                            Add Listing Item
                        </button>
                    </div>

                    {/* Pagination */}
                    {items.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end',padding: "0 20px 20px 20px", }}>
                                         <span style={{ fontSize: '12px', fontWeight: 400, color: '#212529', fontFamily: 'Montserrat, sans-serif' }}>
                                            {(page - 1) * PAGE_SIZE + 1} - {(page - 1) * PAGE_SIZE + pagedItems.length} of {items.length}
                                        </span>
                                        <Pagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            onPageChange={setPage}
                                        />
                                    </div>
                    )}
                </div>


            </div>
        </div>
    );
}
